import { atom, selector } from 'recoil';

import { UserSettings } from '@types';

import {
  AlertsHeadline,
  AlertType,
  ANFState,
  DailyForecastData,
  HourlyForecastData,
  LiveVideoPlayerStateType,
  LocationState,
  OnboardingLandingStateDefault,
  OnboardingPlans,
  PlaylistItem,
  PollenIndexState,
  UserAuthState,
  VodLadingState,
  VodPlaylistState,
  WeatherState,
} from './types';
export type { PlaylistItemSeries } from './types/state';
export { PageProvider } from './PageContextProvider';

export type {
  DailyForecastData,
  HourlyForecastData,
  LocationState,
  OnboardingLandingStateDefault,
  PollenIndexState,
  UserAuthState,
  PlaylistItem,
  VodLadingState,
  VodPlaylistState,
  WeatherState,
  AlertType,
  AlertsHeadline,
  OnboardingPlans,
  ANFState,
  LiveVideoPlayerStateType,
};

import { AxiosError } from 'axios';

import { OverlayType, ZoomPreset } from '../components/map/MapTypes';
import { DEBUG, QA } from '../helpers/Platform';

// This is not used for now but will need when using real-time remote config in the future
// export const configState = atom({
//   key: `config`,
//   default: {
//     api: {
//       dsp: null,
//       twc: null,
//       mapbox: null,
//       adobe_tve: null,
//       auth0: null,
//       segment: null,
//       localnow: null,
//     },
//     constants: {},
//   },
// });

type AddDefaultStateProperties<T> = {
  data?: T;
  error: unknown;
  isFetched: boolean;
  isLoading: boolean;
};

export const ldlPlaylistState = atom({
  key: 'ldlPlaylistState',
  default: {
    activeProductIndex: 0,
    isAnimationEnabled: true,
    isSevereCrawlEnabled: true,
    nextProductIndex: 0,
  },
});

export const ldlShouldPlay = selector({
  key: 'ldlShouldPlay',
  get: ({ get }) => {
    const { activeProductIndex, nextProductIndex } = get(ldlPlaylistState);

    return nextProductIndex !== activeProductIndex;
  },
});

type MapTypeState = {
  overlay: keyof typeof OverlayType;
  zoom: keyof typeof ZoomPreset;
};

export const mapTypeState = atom<MapTypeState>({
  key: 'mapType',
  default: {
    overlay: OverlayType.radar,
    zoom: ZoomPreset.local,
  },
});

export const locationState = atom<LocationState>({
  key: 'locationState',
  default: {
    city: null,
    coords: {
      lat: null,
      lng: null,
    },
    state: null,
    zipCode: null,
    market: null,
  },
});

export const screenReaderEnabledState = atom({
  key: 'screenReaderEnabledState',
  default: false,
});

export const networkStatusState = atom({
  key: 'networkStatusState',
  default: {
    connected: true,
    connectionType: 'WiFi',
  },
});

export const hdmiConnectedState = atom({
  key: 'hdmiConnectedState',
  default: true,
});

export const hourlyWeatherPageFocusIndex = atom({
  key: 'hourlyWeatherPageFocusIndex',
  default: 0,
});

export const dayDateTimeState = atom({
  key: 'dayDateTimeState',
  default: {
    day: '',
    date: '',
    time: '',
    utcTime: '',
    timeZone: '',
  },
});

export const userSettingsState = atom<UserSettings>({
  key: 'userSettings',
  default: {
    adobeToken: '', // ADDED FROM TYPE
    cableProviderName: null,
    cableProviderId: null,
    cableProviderLogoUrl: null,
    closedCaptionsEnabled: false,
    deviceInfoString: null,
    dspCustomerToken: null, // this returned after success for Tizen or X1 device activation
    fetchedFromStorage: false,
    isCableSubscriber: false,
    appAccessed: false,
    firstName: null,
    lastName: null,
    email: null,
    pricePlan: null,
    isPaidSubscriber: false,
    billDate: null,
    locations: [
      {
        id: 0,
        city: '', // ADDED FROM TYPE
        isDefault: true,
        isWatching: true,
        name: null,
        fullName: null,
        state: null,
        coords: {
          lat: null,
          lng: null,
        },
        zipCode: null,
        timeZone: null,
        market: null,
      },
    ],
    alertsEnabled: true,
    trialStart: null,
    subscriptionStatus: null,
    priorSubscriptionStatus: null,
    dspCustomerId: null,
    substart: null,
    scheduledcancellationat: null,
  },
});

export const liveVideoPlayerState = atom<LiveVideoPlayerStateType>({
  key: 'liveVideoPlayer',
  default: {
    data: null,
    error: null,
    isLoading: false,
    isFetched: false,
    adTrackingData: null,
    height: 0,
    width: 0,
    // stopped is used for both web and android but especially if android app is in background we need to stop live video.
    stopped: false,
    /**
     * The following 3 properties are only used for android
     */
    isFullscreen: false,
    isScreenFocused: false,
    nextFocusUp: undefined,
    currentLiveVideoStream: null,
  },
});

export type AlertsHeadlinesState = {
  data: AlertsHeadline[];
  error: null | AxiosError;
  isFetched: boolean;
  isLoading: boolean;
  shownAlerts: string[];
  alertOverlays: AlertsHeadline[];
};

export const alertsHeadlinesState = atom<AlertsHeadlinesState>({
  key: 'alertsHeadlines',
  default: {
    data: [],
    error: null,
    isFetched: false,
    isLoading: false,
    shownAlerts: [],
    alertOverlays: [],
  },
});

export const currentLocationWeatherState = atom<AddDefaultStateProperties<WeatherState>>({
  key: 'currentLocationWeather',
  default: {
    data: undefined,
    error: null,
    isFetched: false,
    isLoading: false,
  },
});

export const airQualityState = atom({
  key: 'airQuality',
  default: {
    data: undefined,
    error: null,
    isFetched: false,
    isLoading: false,
  },
});

export const dailyForecastState = atom<AddDefaultStateProperties<DailyForecastData>>({
  key: 'dailyForecast',
  default: {
    data: undefined,
    error: null,
    isFetched: false,
    isLoading: false,
  },
});

export const hourlyForecastState = atom<AddDefaultStateProperties<HourlyForecastData>>({
  key: 'hourlyForecast',
  default: {
    data: undefined,
    error: null,
    isFetched: false,
    isLoading: false,
  },
});

export const onboardingLandingState = atom<OnboardingLandingStateDefault>({
  key: 'onboardingLanding',
  default: {
    data: undefined,
    error: null,
    isFetched: false,
    isLoading: false,
  },
});

export const onboardingPlansState = atom<AddDefaultStateProperties<OnboardingPlans>>({
  key: 'onboardingChoosePlan',
  default: {
    data: undefined,
    error: null,
    isFetched: false,
    isLoading: false,
  },
});

export const pollenIndexState = atom<AddDefaultStateProperties<PollenIndexState>>({
  key: 'pollenIndex',
  default: {
    data: undefined,
    error: null,
    isFetched: false,
    isLoading: false,
  },
});

export const anfState = atom<ANFState>({
  key: 'anfAlerts',
  default: {
    data: undefined,
    error: null,
    isFetched: false,
    isLoading: false,
    shownAlerts: [],
  },
});

export const userAuthState = atom<AddDefaultStateProperties<UserAuthState>>({
  key: 'userAuth',
  default: {
    data: undefined,
    error: null,
    isFetched: false,
    isLoading: false,
  },
});

export const userEmailState = atom<string | null>({
  key: 'userEmail',
  default: null,
});

export const hasResetRefetchIntervalState = atom({
  key: 'hasResetRefetchInterval',
  default: {
    airQuality: false,
    alertHeadlines: false,
    currentLocationWeather: false,
    dailyWeather: false,
    hourlyWeather: false,
    pollenIndex: false,
  },
});

export const vodLadingState = atom<AddDefaultStateProperties<VodLadingState>>({
  key: 'vodLanding',
  default: {
    data: {},
    error: null,
    isFetched: false,
    isLoading: false,
  },
});

export const onboardingFlowState = atom({
  key: 'onboardingFlow',
  default: {
    signUp: false,
    secondTime: false,
  },
});

export const vodPlaylistState = atom<VodPlaylistState>({
  key: 'vodPlaylist',
  default: {
    playlist: [],
    title: null,
    ignoreCurrent: true,
    currentID: null,
  },
});

export const weatherForecastState = atom({
  key: 'weatherForecastCounter',
  default: 0,
});

export const environmentsState = atom({
  key: 'environmentsState',
  default: {
    // If the debug is true the app will be able to switch between environments
    // state based on manifest conf
    overrideEnv: DEBUG,
    environments: {
      prod: !DEBUG,
      debug: DEBUG,
      qa: QA,
    },
  },
});
